import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { profileApi, type AppProfile } from '@/api/auth';
import { fetchRecentOrders } from '@/api/order';
import { getToken } from '@/lib/auth-storage';
import { DEFAULT_AVATAR_URL } from '@/constants/avatar';
import type { MhOrderRow } from '@/types/mh';
import { formatBalanceAmount, formatBalanceU } from '@/utils/product-display';
import { getOrderStatusStyle } from '@/utils/order-display';

function formatCnDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatMemberStatus(user: AppProfile): string {
  if (user.memberPermanent === '1') {
    return user.memberLevelName ? `${user.memberLevelName} · 永久` : '永久会员';
  }
  if (user.memberExpireTime) {
    const level = user.memberLevelName || '会员';
    return `${level} · 至 ${formatCnDate(user.memberExpireTime)}`;
  }
  return user.memberLevelName || '普通用户';
}

function formatOrderDate(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

const quickActions = [
  { icon: 'ri-add-circle-line', label: '创建新订单', desc: '选择服务开始查询', path: '/services', color: 'bg-blue-50 text-blue-500' },
  { icon: 'ri-bank-card-line', label: '充值余额', desc: '为账户充值', path: '/recharge', color: 'bg-emerald-50 text-emerald-500' },
  { icon: 'ri-gift-line', label: '推广好友', desc: '查看收益与链接', path: '/affiliate', color: 'bg-orange-50 text-orange-500' },
];

const navCards = [
  { icon: 'ri-file-list-3-line', label: '我的订单', desc: '查看订单历史', path: '/orders' },
  { icon: 'ri-wallet-3-line', label: '我的钱包', desc: '余额与交易', path: '/wallet' },
  { icon: 'ri-gift-line', label: '推广好友', desc: '收益与推广链接', path: '/affiliate' },
  { icon: 'ri-bank-card-line', label: '收款绑定', desc: 'U地址与银行卡', path: '/payment-binding' },
  { icon: 'ri-exchange-dollar-line', label: '申请提现', desc: '提交提现订单', path: '/withdraw' },
  { icon: 'ri-settings-3-line', label: '账户设置', desc: '个人信息设置', path: '/settings' },
];

export default function ProfilePage() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [user, setUser] = useState<AppProfile | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [recentOrders, setRecentOrders] = useState<MhOrderRow[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState('');

  const loadProfile = useCallback(async () => {
    if (!getToken()) {
      navigate('/login');
      return;
    }
    setLoading(true);
    setOrdersLoading(true);
    setLoadError('');
    setOrdersError('');
    try {
      const [profileRes, ordersRes] = await Promise.all([profileApi(), fetchRecentOrders(5)]);
      if (profileRes.code !== 0 || !profileRes.data) {
        setLoadError(profileRes.msg || '加载失败');
        setUser(null);
      } else {
        setUser(profileRes.data);
      }
      if (ordersRes.code === 0 && Array.isArray(ordersRes.data)) {
        setRecentOrders(ordersRes.data);
      } else {
        setRecentOrders([]);
        setOrdersError(ordersRes.msg || '订单加载失败');
      }
    } catch {
      setLoadError('网络异常');
      setRecentOrders([]);
      setOrdersError('订单加载失败');
    } finally {
      setLoading(false);
      setOrdersLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const handleCopyCode = () => {
    if (!user?.referralCode) return;
    navigator.clipboard.writeText(user.referralCode).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f7f8]">
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-8 pb-24 md:pb-8">

        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">用户中心</h1>
          <p className="text-sm text-gray-400 mt-1">管理您的个人信息和账户设置</p>
        </div>

        {loadError && (
          <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-100 text-sm text-red-600 flex items-center justify-between gap-3">
            <span>{loadError}</span>
            <button type="button" onClick={() => void loadProfile()} className="text-gray-700 font-medium hover:underline whitespace-nowrap">
              重试
            </button>
          </div>
        )}

        {/* User Info Card */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
          {loading && (
            <div className="text-center py-12 text-gray-400 text-sm">加载中…</div>
          )}
          {!loading && user && (
          <>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                  <img
                    src={DEFAULT_AVATAR_URL}
                    alt="avatar"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="absolute bottom-0.5 right-0.5 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                  <i className="ri-check-line text-white text-[8px]" />
                </span>
              </div>

              {/* Name & badge */}
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xl font-bold text-gray-900">{user.userName}</span>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full font-medium">{user.memberLevelName}</span>
                </div>
                <div className="mt-2 space-y-1">
                  <div className="flex items-center gap-1.5 text-sm text-gray-500">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-line text-gray-400" />
                    </div>
                    <span className="truncate">{user.email}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-gray-500">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-global-line text-gray-400" />
                    </div>
                    <span>IP: {user.loginIp || '—'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Refresh */}
            <button
              type="button"
              onClick={() => void loadProfile()}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-gray-400 hover:text-gray-600"
              title="刷新"
            >
              <i className="ri-refresh-line text-lg" />
            </button>
          </div>

          {/* Meta info row */}
          <div className="mt-4 pt-4 border-t border-gray-50 flex flex-wrap gap-x-8 gap-y-2">
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-gray-400" />
              </div>
              <span>注册: <span className="text-gray-700 font-medium">{formatCnDate(user.registerTime)}</span></span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-key-2-line text-gray-400" />
              </div>
              <span>推荐码:
                <button
                  onClick={handleCopyCode}
                  className="ml-1.5 font-semibold text-gray-800 hover:text-gray-600 cursor-pointer transition-colors"
                  title="点击复制"
                >
                  {user.referralCode}
                </button>
                {copied && <span className="ml-1 text-emerald-500 text-xs">已复制!</span>}
              </span>
            </div>
          </div>

          {/* Status bar */}
          <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-sm">
                <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                <span className="text-gray-600">账户状态: 正常</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shield-check-line text-emerald-500" />
                </div>
                <span>账户安全</span>
              </div>
            </div>
            <span className="text-xs text-gray-400">用户ID: {user.userId}</span>
          </div>
          </>
          )}
        </div>

        {/* Stats Row */}
        {!loading && user && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-5">
          <Link to="/wallet" className="bg-white rounded-2xl border border-gray-100 p-4 text-center hover:border-gray-200 transition-colors">
            <div className="text-2xl font-bold text-gray-900 flex items-baseline justify-center gap-0.5">
              <span>{formatBalanceAmount(user.walletBalance)}</span>
              <span className="text-sm font-medium text-gray-500">U</span>
            </div>
            <div className="text-xs text-gray-400 mt-1">账户余额</div>
          </Link>
          <div className="bg-white rounded-2xl border border-gray-100 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{user.pointsBalance ?? 0}</div>
            <div className="text-xs text-gray-400 mt-1">积分</div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4 text-center flex flex-col justify-center min-h-[72px]">
            <div className="text-sm font-semibold text-gray-900 leading-snug px-1">{formatMemberStatus(user)}</div>
            <div className="text-xs text-gray-400 mt-1">会员</div>
          </div>
        </div>
        )}

        {/* Function Nav */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
          <h2 className="text-base font-semibold text-gray-800 mb-4">功能导航</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {navCards.map((card) => (
              <Link
                key={card.label}
                to={card.path}
                className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-all cursor-pointer group"
              >
                <div className="w-11 h-11 flex items-center justify-center bg-gray-50 rounded-xl group-hover:bg-gray-100 transition-colors">
                  <i className={`${card.icon} text-xl text-gray-600`} />
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-800">{card.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{card.desc}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
          <h2 className="text-base font-semibold text-gray-800 mb-4">快速操作</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
            {quickActions.map((action) => (
              <Link
                key={action.label}
                to={action.path}
                className="flex items-center gap-3 p-4 rounded-xl border border-dashed border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all cursor-pointer group"
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${action.color}`}>
                  <i className={`${action.icon} text-lg`} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-800">{action.label}</div>
                  <div className="text-xs text-gray-400">{action.desc}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-800">最近订单</h2>
            <Link to="/orders" className="text-sm text-gray-400 hover:text-gray-700 transition-colors cursor-pointer">
              查看全部 <i className="ri-arrow-right-s-line" />
            </Link>
          </div>
          {ordersError && !ordersLoading && (
            <p className="text-xs text-amber-600 mb-3">{ordersError}</p>
          )}
          {ordersLoading && (
            <div className="py-8 text-center text-sm text-gray-400">加载订单中…</div>
          )}
          {!ordersLoading && recentOrders.length === 0 && (
            <div className="py-8 text-center">
              <p className="text-sm text-gray-400 mb-3">暂无订单</p>
              <Link
                to="/services"
                className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                去下单 <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
          )}
          {!ordersLoading && recentOrders.length > 0 && (
            <div className="space-y-3">
              {recentOrders.map((order) => {
                const st = getOrderStatusStyle(order.status);
                return (
                  <div
                    key={order.orderId ?? order.orderNo}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3 border-b border-gray-50 last:border-0"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 flex items-center justify-center bg-gray-50 rounded-lg flex-shrink-0">
                        <i className="ri-file-search-line text-gray-500" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{order.productName}</div>
                        <div className="text-xs text-gray-400 mt-0.5 font-mono truncate">
                          {order.orderNo}
                          {order.createTime ? ` · ${formatOrderDate(order.createTime)}` : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-start gap-3 flex-shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.compactClassName}`}>
                        {st.label}
                      </span>
                      <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                        {formatBalanceU(order.amount)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </main>
      <Footer />
    </div>
  );
}
